import { anyPass, isEmpty, isNil, reject as rejectProps } from 'ramda';
import { log } from 'log';
import { getBaseDomain } from 'js/utils/url.util';
import { getScript } from 'js/utils/scripts.util';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as constants from './contants';

let isConfigSet = false;
const iapiInitialConfig = {};
let isIntegrationScriptLoadingStarted = false;

const integrationScriptLoadState = Promise.withResolvers();
const pasCallChainMap = new Map();

// region private

/**
 * Get integration.js script from PAS server
 * @returns {Promise}
 */
export const getIntegrationScript = () => {
    if (!isConfigSet || isIntegrationScriptLoadingStarted) {
        return integrationScriptLoadState.promise;
    }

    const onStaticPASLoadCallbackName = 'iapiOnPASLoad';
    const pathCollected = `https://${iapiInitialConfig.loginServer}/jswrapper/integration.js.php`
        + '?min=1'
        + '&staticjs=1'
        + `&casino=${iapiInitialConfig.casinoname}`
        + `&onStaticLoad=${onStaticPASLoadCallbackName}`;
    const path = (iapiInitialConfig.ssoPasJsURLs || pathCollected)
        .replace('[superdomain]', getBaseDomain());

    const onGetScriptSuccess = () => {
        if (typeof window.iapiBaseLogin === 'undefined') {
            return;
        }

        Object.assign(window.iapiConf, rejectProps(anyPass([isEmpty, isNil]), iapiInitialConfig));

        integrationScriptLoadState.resolve();
    };

    const onGetScriptFail = () => {
        integrationScriptLoadState.reject({
            data: {
                errorCode: -1,
                errorMessage: `Network or PAS server failure, can't load: ${path}`
            }
        });
    };

    window[onStaticPASLoadCallbackName] = (event) => {
        if (event.type === 'load') {
            onGetScriptSuccess();
        } else {
            onGetScriptFail();
        }
    };

    isIntegrationScriptLoadingStarted = true;

    // IAPI script was loaded earlier
    if (window.iapiConf) {
        integrationScriptLoadState.resolve();
    } else if (iapiInitialConfig.loginServer) {
        getScript(path, { attrs: { async: true } })
            .then(onGetScriptSuccess)
            .catch(onGetScriptFail);
    } else {
        integrationScriptLoadState.reject({
            data: {
                errorCode: -1,
                errorMessage: '"ssoPasDomain" not configured!'
            }
        });
    }

    return integrationScriptLoadState.promise;
};

/**
 * Call PAS action by name
 * @param {string} actionName
 * @param {string} callbackName
 * @param {Array} args
 * @returns {Promise}
 */
export const action = async (actionName, callbackName, args) => {
    await getIntegrationScript();

    const promiseWithResolves = Promise.withResolvers();

    if (pasCallChainMap.has(actionName)) {
        try {
            // this needs to be retrieved before the promise is deleted
            // then deleted and only then awaited
            // in order to guarantee a single call of the action
            const promise = pasCallChainMap.get(actionName);

            pasCallChainMap.set(actionName, promiseWithResolves.promise);

            await promise;
        } catch (error) {
            // previous request failed with timeout or with error
            // but we don't care about it
        }
    } else {
        pasCallChainMap.set(actionName, promiseWithResolves.promise);
    }

    const description = actionName
        .split('iapi')[1]
        .match(/[A-Z][a-z]+/g)
        .join(' ')
        .toLowerCase();

    log('pasSend', {
        args,
        actionName,
        callbackName,
        description
    });

    if (!callbackName) {
        // in case there is no callbackName
        // then this is just a setter call

        try {
            const response = window[actionName].apply(null, args);

            log('pasReceived', {
                response,
                actionName,
                callbackName,
                description
            });

            promiseWithResolves.resolve();
        } catch (error) {
            // in case there will be a synchronous error in the call to iapi* function
            promiseWithResolves.reject(error);
        }

        return;
    }

    const timestamp = Date.now();
    const timeout = setTimeout(() => {
        const errorResponse = {
            errorCode: -1,
            errorMessage: 'PAS Request timeout'
        };

        promiseWithResolves.reject(errorResponse);
    }, constants.REQUEST_TIMEOUT);

    try {
        // only set callback if it is set
        window.iapiSetCallout(window[callbackName], (response) => {
            clearTimeout(timeout);

            if (response?.errorCode) {
                promiseWithResolves.reject(response);

                return;
            }

            promiseWithResolves.resolve(response);
        });

        window[actionName].apply(null, args);
    } catch (error) {
        // in case there will be a synchronous error in the call to iapi* function
        promiseWithResolves.reject(error);
    }

    return promiseWithResolves
        .promise
        .then((response) => {
            if (!callbackName) {
                // in case there is no callbackName
                // then this is just a setter call
                return;
            }

            log('pasReceived', {
                response,
                actionName,
                callbackName,
                description
            });

            publicEventsAPIService.trigger('pas-response', {
                actionName,
                time: Date.now() - timestamp
            });

            return { data: response };
        })
        .catch((error) => {
            log('pasError', {
                actionName,
                description,
                error
            });

            log('pasInfo', [
                'The above error occurred because PAS returned an error during request.',
                'Please expand all objects within the error response above.',
                'Share it to IMS UAM team to retrieve the original server log for the error.'
            ].join('\n'));

            publicEventsAPIService.trigger('pas-error', {
                actionName,
                error,
                time: Date.now() - timestamp
            });

            throw { data: error };
        });
};

/**
 * Call PAS action by name
 * @param {string} actionName
 * @param {object} getParams
 * @param {object} postParams
 * @returns {Promise}
 */
export const actionDirect = async (actionName, getParams, postParams) => {
    const promiseWithResolves = Promise.withResolvers();

    const description = actionName
        .match(/[A-Z][a-z]+/g)
        .join(' ')
        .toLowerCase();

    log('pasSend', {
        actionName,
        getParams,
        postParams,
        description
    });

    const timestamp = Date.now();
    const timeout = setTimeout(() => {
        const errorResponse = {
            errorCode: -1,
            errorMessage: 'PAS Request timeout'
        };

        promiseWithResolves.reject(errorResponse);
    }, constants.REQUEST_TIMEOUT);

    const formData = new FormData();

    formData.append('responseType', 'json');

    Object.entries(postParams)
          .forEach(([key, value]) => formData.append(key, value));

    const url = new URL(`https://${iapiInitialConfig.loginServer}/${actionName}.php`);

    url.searchParams.append('casinoname', iapiInitialConfig.casinoname);
    url.searchParams.append('realMode', '1');
    url.searchParams.append('clientType', iapiInitialConfig.clientType);
    url.searchParams.append('clientPlatform', iapiInitialConfig.clientPlatform);
    url.searchParams.append('deliveryPlatform', iapiInitialConfig.deliveryPlatform);
    url.searchParams.append('deviceId', iapiInitialConfig.deviceId);
    url.searchParams.append('clientSkin', iapiInitialConfig.clientSkin);
    url.searchParams.append('errorLevel', '1');

    Object.entries(getParams)
          .forEach(([key, value]) => url.searchParams.append(key, value));

    fetch(url, {
        body: formData,
        method: 'POST',
        mode: 'cors',
        credentials: 'include'
    })
        .then((response) => {
            clearTimeout(timeout);

            return response.json();
        })
        .then((data) => promiseWithResolves.resolve(data))
        .catch((error) => promiseWithResolves.reject(error));

    return promiseWithResolves
        .promise
        .then((response) => {

            log('pasReceived', {
                response,
                actionName,
                description
            });

            publicEventsAPIService.trigger('pas-response', {
                actionName,
                time: Date.now() - timestamp
            });

            return { data: response };
        })
        .catch((error) => {
            log('pasError', {
                actionName,
                description,
                error
            });

            log('pasInfo', [
                'The above error occurred because PAS returned an error during request.',
                'Please expand all objects within the error response above.',
                'Share it to IMS UAM team to retrieve the original server log for the error.'
            ].join('\n'));

            publicEventsAPIService.trigger('pas-error', {
                actionName,
                error,
                time: Date.now() - timestamp
            });

            throw { data: error };
        });
};

// endregion

// region public

export const init = (app) => {
    Object.assign(iapiInitialConfig, {
        loginServer: app.ssoPasDomain,
        casinoname: app.casinoName,
        clientSkin: app.clientSkin,
        clientType: app.clientType,
        ssoPasJsURLs: app.ssoPasJsURLs,
        ssoPasExternalJsUrl: app.ssoPasExternalJsUrl,
        systemId: 0,
        errorLevel: 1
    });

    isConfigSet = true;

    return integrationScriptLoadState
        .promise
        .catch((error) => {
            log('pasError', {
                actionName: 'load',
                description: error?.data?.errorMessage ?? error?.errorMessage ?? String(error),
                error
            });

            log('pasInfo', [
                'The above error occurred because PAS script produced an error during loading.',
                'Please expand all objects within the error response above.',
                'Share it with IMS UAM team to review.'
            ].join('\n'));

            publicEventsAPIService.trigger('pas-error', {
                actionName: 'load',
                error,
                time: 0
            });

            throw error;
        });
};

/**
 * Login player to PAS
 * where params are [email|user, pass, realMode, language, systemId, serviceType, rememberMeLogin]
 * @param {Object} options
 * @param {string | null} options.userName
 * @param {string} options.password
 * @param {string} [options.loginMode] 'email', 'userName' or externalToken
 * @param {string} options.languageCode
 * @param {boolean} [options.rememberMeLogin]
 * @returns {Promise}
 */
export const login = (options) => {
    let loginAction = 'iapiLoginAndGetTempToken';
    let callbackName = 'iapiCALLOUT_LOGINANDGETTEMPTOKEN';
    let needsTokenRequest = false;

    switch (options.loginMode) {
        case 'email': {
            loginAction = 'iapiEmailLoginAndGetTempToken';
            break;
        }

        case 'externalToken': {
            loginAction = 'iapiLoginExternalTokenAndGetTempToken';
            break;
        }

        case 'printedTokenCode': {
            // change to iapiLoginPrintedIdTokenCodeAndPinAndGetTempToken once it is done by IMS
            loginAction = 'iapiLoginPrintedIdTokenCodeAndPin';
            callbackName = 'iapiCALLOUT_LOGIN';
            needsTokenRequest = true;
            break;
        }

        default: {
            break;
        }
    }

    let params = [
        options.userName, options.password, 1, options.languageCode
    ];

    if (typeof options.rememberMeLogin === 'boolean') {
        params = params.concat([null, null, options.rememberMeLogin]);
    }

    return action(loginAction, callbackName, params)
        .catch((error) => {
            const response = error.response || error;

            if (response?.data?.sessionValidationData) {
                return response;
            }

            return Promise.reject(error);
        })
        .then(async (response) => {
            if (needsTokenRequest) {
                response.data.needsTokenRequest = true;
            }

            return response;
        });
};

/**
 * Login by link from forgotPassword email.
 * where params are [cryptoToken, language]
 * @param {Object} options
 * @param {string} options.fpActionToken
 * @param {string} options.languageCode
 * @returns {Promise}
 */
export const forgotPassToken = options => action(
    'iapiLoginCryptoToken', 'iapiCALLOUT_LOGIN', [options.fpActionToken, options.languageCode]);

/**
 * Get temporary token
 * where params are [realMode, systemId, serviceType]
 * @returns {Promise}
 */
export const getTempToken = () => action(
    'iapiRequestTemporaryToken', 'iapiCALLOUT_TEMPORARYTOKEN', [1]);

/**
 * Get temporary token but without the need to have integration.js loaded.
 * @returns {Promise}
 */
export const getTempTokenDirect = () => actionDirect('GetTemporaryAuthenticationToken', { serviceType: 'LoginService' }, {});

/**
 * Get waiting messages from IMS.
 * where params are [messageTypes, realMode]
 * @param {Object} options
 * @param {string} options.messageTypes
 * @param {number} [options.realMode]
 * @returns {Promise}
 */
export const getWaitingMessages = (options) => action(
    'iapiGetWaitingMessages', 'iapiCALLOUT_MESSAGES', [options.messageTypes, options.realMode]);

/**
 * Get waiting messages from IMS without the need to have integration.js loaded.
 * @param {Object} options
 * @param {string} options.messageTypes
 * @returns {Promise}
 */
export const getWaitingMessagesDirect = ({ messageTypes }) => actionDirect('GetWaitingMessages', { messageTypes }, {});

export const getCaptchaPublicKeyDirect = async () => {
    const url = new URL(`https://${iapiInitialConfig.loginServer}/CaptchaPublicKey.php`);

    url.searchParams.append('casinoname', iapiInitialConfig.casinoname);

    const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include'
    });

    return await response.json();
};

/**
 * Logout from PAS
 * where params are [allSessions, realMode, behavior, invalidateRememberMe]
 * behavior can be one of "this", "all", "hierarchical"
 * @returns {Promise}
 */
export const logout = ({
    allSessions = 1,
    logoutBehavior = 'all'
} = {}) => action('iapiLogout', 'iapiCALLOUT_LOGOUT', [allSessions, 1, logoutBehavior, 1]);

/**
 * Change password on PAS
 * where params are [oldPassword, newPassword, changePassword, realMode]
 * @param {Object} options
 * @param {string} [options.oldPassword]
 * @param {string} options.password
 * @returns {Promise}
 */
export const changePassword = options => action(
    'iapiValidatePasswordChange', 'iapiCALLOUT_SESSIONVALIDATION',
    [options.oldPassword, options.password, 1, 1]);

/**
 * Set new terms&conditions on PAS
 * where params are [reference, accept, realMode]
 * @param {Object} options
 * @param {string} options.termVersionReference
 * @param {boolean} options.accept
 * @returns {Promise}
 */
export const setTerms = options => action(
    'iapiValidateTCVersion', 'iapiCALLOUT_SESSIONVALIDATION',
    [options.termVersionReference, options.accept, 1]);

/**
 * where params are [username, email, birthDate, realMode, verificationAnswer,
 * passwordRecoveryDestinationLink, notificationChannels]
 * @param {Object} options
 * @param {string} [options.userName]
 * @param {string} [options.email]
 * @param {string} [options.birthDate]
 * @param {string} [options.verificationAnswer]
 * @param {string} options.passwordRecoveryDestinationLink
 * @param {string} [options.notificationChannels]
 * @returns {Promise}
 */
export const forgotPassword = options => {
    const params = [
        options.userName || '',
        options.email || '',
        options.birthDate || '',
        1,
        options.verificationAnswer || null,
        options.passwordRecoveryDestinationLink
    ];

    if (options.notificationChannels) {
        params.push(options.notificationChannels);
    }

    return action('iapiForgotPassword', 'iapiCALLOUT_FORGOTPASSWORD', params);
};

/**
 * where params are [realMode]
 * @returns {Promise}
 */
export const getLoggedInPlayer = () => new Promise((resolve) => {
    const cookieExists = document.cookie.includes(`pas[${iapiInitialConfig.casinoname}][real][isOnline]=1`);

    resolve({
        data: {
            cookieExists: cookieExists ? '1' : '0'
        }
    });
});

/**
 * Two factor validation of token
 * @param {string} token
 * @param {number} isDeviceTrusted
 * @returns {Promise}
 */
export const validateTwoFactorToken = (token, isDeviceTrusted) => action(
    'iapiValidateTwoFactorToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [token, isDeviceTrusted]);

/**
 * Multi factor validation of token
 * @param {string} json
 * @returns {Promise}
 */
export const validateMfaToken = (json) => action(
    'iapiValidateMfaToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [json]);

/**
 * Geolocation validation
 * @param {number | null} latitude
 * @param {number | null} longitude
 * @param {string | null} geoPacket
 * @param {string | null} transactionId
 * @param {string | null} error
 * @param {string | null} [geolocationType]
 * @returns {Promise}
 */
export const validateGeolocation = (latitude, longitude, geoPacket, transactionId, error, geolocationType) =>
    window.Playtech.Variables.useDirectPASRequests
        ? actionDirect('ValidateLoginSession', { validationType: 'Geolocation' }, {
            latitude,
            longitude,
            geoPacket,
            transactionId,
            error,
            geolocationType
        })
        : action(
            'iapiValidateGeolocation', 'iapiCALLOUT_SESSIONVALIDATION',
            [latitude, longitude, geoPacket, transactionId, error, geolocationType]);

/**
 * Pin verification
 * @param {string} pin
 * @returns {Promise}
 */
export const validatePin = pin => action('iapiValidatePinToken', 'iapiCALLOUT_SESSIONVALIDATION', [pin]);

/**
 * FitToPlay Token validation
 * @param {boolean} isFitToPlay
 * @returns {Promise}
 */
export const validateFitToPlayToken = isFitToPlay => action(
    'iapiValidateFitToPlayToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [isFitToPlay]);

/**
 * Resend 2FA token upon login
 * @returns {Promise}
 */
export const resendLogin2FAToken = () => action('iapiResendLogin2FAToken', 'iapiCALLOUT_RESENDLOGIN2FATOKEN', []);

export const setIovationBlackbox = blackbox => action('iapiSetIovationBlackbox', null, [blackbox]);

export const setDeviceId = deviceId => action('iapiSetDeviceId', null, [encodeURIComponent(deviceId)]);

export const setDeliveryPlatform = deliveryPlatform => action('iapiSetDeliveryPlatform', null, [deliveryPlatform]);

export const setClientPlatform = clientPlatform => action('iapiSetClientPlatform', null, [clientPlatform]);

export const setClientType = clientType => action('iapiSetClientType', null, [clientType]);

/**
 * Used in reCaptcha Login flow
 * @param {Object} challenge
 * @param {string} challenge.responseToken - retrieved from CAPTCHA provider
 * @param {string} challenge.operation - type of operation, e.g. "login", "signup", "checkUsernameAvailability"
 * @param {Function} challenge.callback - upon successful completion the callback is to be invoked with an objects that contains "token"
 *     and "expirationTime"
 */
export const validateChallengeAnswer = (challenge) => {
    // load patch with validateChallengeAnswer function
    if (!window.validateChallengeAnswer) {
        const patchPath = `https://${iapiInitialConfig.loginServer}/jswrapper/validateChallengeAnswer.js`;

        getIntegrationScript()
            .then(() => getScript(patchPath, { attrs: { async: true } }))
            .then(() => {
                window.validateChallengeAnswer(challenge);
            });
    } else {
        window.validateChallengeAnswer(challenge);
    }
};

/**
 * Set login type
 * @param  {string|Undefined}   type
 * @returns {Deferred}
 */
export const setLoginType = type => getIntegrationScript()
    .then(() => {
        if (window.iapiLoginTypes && window.iapiLoginTypes[type]) {
            window.iapiConf.authenticationType = window.iapiLoginTypes[type];
        } else {
            window.iapiConf && delete window.iapiConf.authenticationType;
        }
    })
    .catch(() => {
        log('error', 'Pas is not ready');

        return Promise.reject({
            data: {
                errorCode: -1,
                errorMessage: 'integration.js for PAS is not defined'
            }
        });
    });

export const getFitToPlaySubmissionRequired = () => {
    return window.iapiConf?.fitToPlaySubmissionRequired === '1';
};

// endregion
